/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, ElementRef, Renderer, ViewEncapsulation, HostBinding } from '@angular/core';
import { Optional, Host } from '@angular/core';
import classNames from 'classnames';

import { Row } from './row';
import { DomUtils } from '../util/dom.utils';

export interface ColSize {
    span?: number;
    order?: number;
    offset?: number;
    push?: number;
    pull?: number;
}
/**
 * 24 栅格系统 之 Column
 * 布局的栅格化系统，我们是基于行（row）和列（col）来定义信息区块的外部框架，以保证页面的每个区域能够稳健地排布起来。下面简单介绍一下它的工作原理：
 * 
 * - 通过`row`在水平方向建立一组`column`（简写col）
 * - 你的内容应当放置于`col`内，并且，只有`col`可以作为`row`的直接元素
 * - 栅格系统中的列是指1到24的值来表示其跨越的范围。例如，三个等宽的列可以使用`.col-8`来创建
 * - 如果一个`row`中的`col`总和超过 24，那么多余的`col`会作为一个整体另起一行排列
 */
export class Col {
/**
 * 样式前缀
 */
prefixCls = 'ant-col';

    /* 栅格占位格数，为 0 时相当于 display: none */
     span: number;
/**
 * 栅格顺序，flex 布局模式下有效
 */
order = 0;
/**
 * 栅格左侧的间隔格数，间隔内不可以有栅格
 */
offset = 0;
/**
 * 栅格向右移动格数
 */
push = 0;
/**
 * 栅格向左移动格数
 */
pull = 0;
/**
 * <768px 响应式栅格，可为栅格数或一个包含其他属性的对象
 */
xs: number | ColSize;
/**
 * >=768px 响应式栅格，可为栅格数或一个包含其他属性的对象
 */
sm: number | ColSize;
/**
 * ≥992px 响应式栅格，可为栅格数或一个包含其他属性的对象
 */
md: number | ColSize;
/**
 * ≥1200px 响应式栅格，可为栅格数或一个包含其他属性的对象
 */
lg: number | ColSize;
/**
 * ≥1600px 响应式栅格，可为栅格数或一个包含其他属性的对象
 */
xl: number | ColSize;
/**
 * 用户CSS样式
 */
class: string;
/**
 * @param {?} renderer
 * @param {?} _elementRef
 * @param {?} row
 */
constructor(
private renderer: Renderer,
private _elementRef: ElementRef,
private row: Row) {

    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this._elementRef.nativeElement as HTMLElement));
    }
/**
 * 最终样式
 * @return {?}
 */
get classes(): string {
        let /** @type {?} */ sizeClassObj = {};

        ['xs', 'sm', 'md', 'lg', 'xl'].forEach(size => {

            let /** @type {?} */ sizeProps: ColSize = {};
            if (typeof this[size] === 'number') {
                sizeProps.span = this[size];
            } else if (typeof this[size] === 'object') {
                sizeProps = this[size] || {};
            }

            sizeClassObj = Object.assign(sizeClassObj, {
                [`${this.prefixCls}-${size}-${sizeProps.span}`]: sizeProps.span !== undefined,
                [`${this.prefixCls}-${size}-order-${sizeProps.order}`]: sizeProps.order || sizeProps.order === 0,
                [`${this.prefixCls}-${size}-offset-${sizeProps.offset}`]: sizeProps.offset || sizeProps.offset === 0,
                [`${this.prefixCls}-${size}-push-${sizeProps.push}`]: sizeProps.push || sizeProps.push === 0,
                [`${this.prefixCls}-${size}-pull-${sizeProps.pull}`]: sizeProps.pull || sizeProps.pull === 0,
            });
        });

        const /** @type {?} */ classes = classNames({
            [`${this.prefixCls}-${this.span}`]: this.span !== undefined && this.span !== null,
            [`${this.prefixCls}-order-${this.order}`]: this.order,
            [`${this.prefixCls}-offset-${this.offset}`]: this.offset,
            [`${this.prefixCls}-push-${this.push}`]: this.push,
            [`${this.prefixCls}-pull-${this.pull}`]: this.pull,
        }, this.class, sizeClassObj);
        return classes;
    }
/**
 * 特殊样式
 * @return {?}
 */
get marginLeft(): any {
        if (this.row && this.row.gutter > 0
            && DomUtils.isParentChild(this.row.getHostElement(), this.getHostElement())) {
            return (this.row.gutter / 2) + 'px';
        }
        return null;
    }
/**
 * 特殊样式
 * @return {?}
 */
get marginRight(): any {
        if (this.row && this.row.gutter > 0
            && DomUtils.isParentChild(this.row.getHostElement(), this.getHostElement())) {
            return (this.row.gutter / 2) + 'px';
        }
        return null;
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-col',
    template: `
        <ng-content></ng-content>
    `,
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
{type: Row, decorators: [{ type: Optional }, { type: Host }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'span': [{ type: Input },],
'order': [{ type: Input },],
'offset': [{ type: Input },],
'push': [{ type: Input },],
'pull': [{ type: Input },],
'xs': [{ type: Input },],
'sm': [{ type: Input },],
'md': [{ type: Input },],
'lg': [{ type: Input },],
'xl': [{ type: Input },],
'class': [{ type: Input },],
'classes': [{ type: HostBinding, args: ['class', ] },],
'marginLeft': [{ type: HostBinding, args: ['style.padding-left', ] },],
'marginRight': [{ type: HostBinding, args: ['style.padding-right', ] },],
};
}

function Col_tsickle_Closure_declarations() {
/** @type {?} */
Col.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Col.ctorParameters;
/** @type {?} */
Col.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
Col.prototype.prefixCls;
/** @type {?} */
Col.prototype.span;
/**
 * 栅格顺序，flex 布局模式下有效
 * @type {?}
 */
Col.prototype.order;
/**
 * 栅格左侧的间隔格数，间隔内不可以有栅格
 * @type {?}
 */
Col.prototype.offset;
/**
 * 栅格向右移动格数
 * @type {?}
 */
Col.prototype.push;
/**
 * 栅格向左移动格数
 * @type {?}
 */
Col.prototype.pull;
/**
 * <768px 响应式栅格，可为栅格数或一个包含其他属性的对象
 * @type {?}
 */
Col.prototype.xs;
/**
 * >=768px 响应式栅格，可为栅格数或一个包含其他属性的对象
 * @type {?}
 */
Col.prototype.sm;
/**
 * ≥992px 响应式栅格，可为栅格数或一个包含其他属性的对象
 * @type {?}
 */
Col.prototype.md;
/**
 * ≥1200px 响应式栅格，可为栅格数或一个包含其他属性的对象
 * @type {?}
 */
Col.prototype.lg;
/**
 * ≥1600px 响应式栅格，可为栅格数或一个包含其他属性的对象
 * @type {?}
 */
Col.prototype.xl;
/**
 * 用户CSS样式
 * @type {?}
 */
Col.prototype.class;
/** @type {?} */
Col.prototype.renderer;
/** @type {?} */
Col.prototype._elementRef;
/** @type {?} */
Col.prototype.row;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
